import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldIcon, ZapIcon, HeartPulseIcon, BrainIcon, CircleIcon, FlaskConicalIcon, ShieldPlusIcon, BatteryChargingIcon, ArrowRightIcon } from 'lucide-react';
const Cordyceps = () => {
  const [activeSection, setActiveSection] = useState('bioenergetics');
  return <div className="w-full bg-white" data-id="element-250">
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 pt-24 pb-16 bg-gradient-to-b from-red-50 to-red-100" data-id="element-251">
        <div className="max-w-4xl mx-auto" data-id="element-252">
          <div className="w-20 h-1 bg-red-500 mx-auto mb-8 rounded-full" data-id="element-253"></div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4" data-id="element-254">
            Energy — Cordyceps
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto mb-10" data-id="element-255">
            Non-caffeinated stamina. Bright botanical finish.
          </p>
        </div>
      </section>

      {/* Benefits Categories Section with Side Navigation */}
      <section className="py-16 px-4 bg-white" data-id="element-256">
        <div className="container mx-auto" data-id="element-257">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-258">
            Cordyceps Spora: 9 Benefit Categories
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto text-center mb-6" data-id="element-259">
            Our proprietary extraction technology unlocks the full spectrum of
            Cordyceps bioactives, delivering nine distinct benefit categories in
            a single cup.
          </p>

          {/* Spec Summary Bar */}
          <div className="max-w-3xl mx-auto bg-gray-50 rounded-xl p-4 mb-16 text-center" data-id="element-260">
            <p className="text-sm font-medium tracking-wide uppercase" data-id="element-261">
              Cordyceps Spora | Bioenergetics + Immunonutrients + Antioxidants
            </p>
          </div>

          {/* Desktop: Sticky side navigation */}
          <div className="hidden md:flex" data-id="element-262">
            <div className="w-64 sticky top-32 self-start pr-8" data-id="element-263">
              <nav className="flex flex-col space-y-4" data-id="element-264">
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'bioenergetics' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('bioenergetics')} data-id="element-265">
                  <div className="flex items-center gap-3" data-id="element-266">
                    <BatteryChargingIcon className="w-5 h-5" data-id="element-267" />
                    <span data-id="element-268">Bioenergetics</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'immunonutrients' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('immunonutrients')} data-id="element-269">
                  <div className="flex items-center gap-3" data-id="element-270">
                    <ShieldPlusIcon className="w-5 h-5" data-id="element-271" />
                    <span data-id="element-272">Immunonutrients</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'antioxidants' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('antioxidants')} data-id="element-273">
                  <div className="flex items-center gap-3" data-id="element-274">
                    <ShieldIcon className="w-5 h-5" data-id="element-275" />
                    <span data-id="element-276">Antioxidants</span>
                  </div>
                </button>
              </nav>
            </div>
            <div className="flex-1 max-w-3xl" data-id="element-277">
              {/* Bioenergetics Section */}
              <section id="bioenergetics" className={`pb-24 ${activeSection === 'bioenergetics' ? 'block' : 'hidden'}`} data-id="element-278">
                <h2 className="text-3xl font-bold mb-8" data-id="element-279">
                  The Energy Architecture of Cordyceps
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-280">
                  Cordyceps enhances cellular energy production, improves oxygen
                  utilization, and supports cardiovascular and respiratory
                  function for sustained stamina.
                </p>
                {/* 3-column layout for desktop */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-281">
                  {/* Bioenergetics */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-282">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-283">
                      <ZapIcon className="w-6 h-6" data-id="element-284" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-285">Bioenergetics</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-286">
                      Your cellular engine
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-287">
                      <span className="font-medium" data-id="element-288">Experience:</span> Sustained
                      stamina and easier breathing.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-289">
                      <span className="font-medium" data-id="element-290">Why it works:</span>{' '}
                      Cordycepin, adenosine, and sterols boost ATP production
                      and oxygen use.
                    </p>
                    <div className="mt-auto" data-id="element-291">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-292">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-293">
                        <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-294">
                          Cordycepin
                        </span>
                        <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-295">
                          Adenosine
                        </span>
                        <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-296">
                          Sterols
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Circulatory Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-297">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-298">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-299" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-300">
                      Circulatory Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-301">
                      Oxygen on demand
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-302">
                      <span className="font-medium" data-id="element-303">Experience:</span> Enhanced
                      endurance and cardiovascular efficiency.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-304">
                      <span className="font-medium" data-id="element-305">Why it works:</span>{' '}
                      Adenosine analogs improve blood flow and oxygen delivery.
                    </p>
                    <div className="mt-auto" data-id="element-306">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-307">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-308">
                        <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-309">
                          Adenosine analogs
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Respiratory Health */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-310">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-311">
                      <div className="w-6 h-6" data-id="element-312" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-313">
                      Respiratory Health
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-314">
                      Breathe with ease
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-315">
                      <span className="font-medium" data-id="element-316">Experience:</span> Deeper,
                      more efficient breathing under stress.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-317">
                      <span className="font-medium" data-id="element-318">Why it works:</span>{' '}
                      Cordycepin and polysaccharides expand lung capacity and
                      oxygen uptake.
                    </p>
                    <div className="mt-auto" data-id="element-319">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-320">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-321">
                        <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-322">
                          Cordycepin
                        </span>
                        <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-323">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Immunonutrients Section */}
              <section id="immunonutrients" className={`pb-24 ${activeSection === 'immunonutrients' ? 'block' : 'hidden'}`} data-id="element-324">
                <h2 className="text-3xl font-bold mb-8" data-id="element-325">
                  The Resilience Matrix
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-326">
                  Cordyceps contains unique compounds that modulate immune
                  function, balance stress response, and support hormonal health
                  for resilience.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-327">
                  {/* Immunonutrients */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-328">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-329">
                      <ShieldPlusIcon className="w-6 h-6" data-id="element-330" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-331">Immunonutrients</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-332">
                      Cellular defense
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-333">
                      Experience resilience under strain and reduced burnout.
                      Beta-glucans and glycoproteins modulate immune system
                      signaling.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-334">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-335">
                        Beta-glucans
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-336">
                        Glycoproteins
                      </span>
                    </div>
                  </div>
                  {/* Adaptogens */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-337">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-338">
                      <CircleIcon className="w-6 h-6" data-id="element-339" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-340">Adaptogens</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-341">
                      Stress equilibrium
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-342">
                      Feel centered during high-pressure situations. Triterpenes
                      harmonize the HPA axis.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-343">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-344">
                        Triterpenes
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-345">
                        Polysaccharides
                      </span>
                    </div>
                  </div>
                  {/* Hormonal Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-346">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-347">
                      <FlaskConicalIcon className="w-6 h-6" data-id="element-348" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-349">Hormonal Support</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-350">
                      Metabolic harmony
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-351">
                      Experience stable energy levels and metabolic balance.
                      Sterols enhance hormone signaling.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-352">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-353">
                        Sterols
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-354">
                        Adenosine derivatives
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Antioxidants Section */}
              <section id="antioxidants" className={`pb-24 ${activeSection === 'antioxidants' ? 'block' : 'hidden'}`} data-id="element-355">
                <h2 className="text-3xl font-bold mb-8" data-id="element-356">
                  The Protection Network
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-357">
                  Cordyceps contains powerful compounds that neutralize free
                  radicals, reduce inflammation, and protect cellular health
                  throughout the body and brain.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-358">
                  {/* Antioxidants */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-359">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-360">
                      <ShieldIcon className="w-6 h-6" data-id="element-361" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-362">Antioxidants</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-363">
                      Cellular shield
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-364">
                      Experience reduced fatigue with steady energy flow.
                      Phenolic compounds neutralize free radicals.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-365">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-366">
                        Phenolic compounds
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-367">
                        Cordycepin
                      </span>
                    </div>
                  </div>
                  {/* Anti-Inflammatory */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-368">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-369">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-370" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-371">
                      Anti-Inflammatory
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-372">
                      Rapid recovery
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-373">
                      Feel joints and muscles recover more quickly. Cordycepin
                      modulates inflammatory pathways.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-374">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-375">
                        Cordycepin
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-376">
                        Sterols
                      </span>
                    </div>
                  </div>
                  {/* Cognitive Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-377">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-378">
                      <BrainIcon className="w-6 h-6" data-id="element-379" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-380">
                      Cognitive Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-381">
                      Mental clarity
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-382">
                      Experience sharper focus and reduced mental fatigue.
                      Nucleosides optimize neurotransmitter balance.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-383">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-384">
                        Cordycepin
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-385">
                        Nucleosides
                      </span>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* Mobile: Collapsible sections */}
          <div className="md:hidden space-y-8" data-id="element-386">
            {/* Spec Summary Bar - Mobile */}
            <div className="bg-gray-50 rounded-xl p-4 mb-8 text-center" data-id="element-387">
              <p className="text-xs font-medium tracking-wide uppercase" data-id="element-388">
                Cordyceps Spora | Bioenergetics + Immunonutrients + Antioxidants
              </p>
            </div>
            <div data-id="element-389">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'bioenergetics' ? '' : 'bioenergetics')} data-id="element-390">
                <div className="flex items-center gap-3" data-id="element-391">
                  <BatteryChargingIcon className="w-5 h-5" data-id="element-392" />
                  <h2 className="text-xl font-bold" data-id="element-393">The Energy Architecture</h2>
                </div>
                <span data-id="element-394">{activeSection === 'bioenergetics' ? '−' : '+'}</span>
              </button>
              {activeSection === 'bioenergetics' && <div className="p-4 space-y-8" data-id="element-395">
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-396">
                    <div className="flex items-center gap-4 mb-3" data-id="element-397">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full" data-id="element-398">
                        <ZapIcon className="w-5 h-5" data-id="element-399" />
                      </div>
                      <div data-id="element-400">
                        <h3 className="font-bold" data-id="element-401">Bioenergetics</h3>
                        <p className="text-xs text-gray-500" data-id="element-402">
                          Your cellular engine
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-403">
                      <span className="font-medium" data-id="element-404">Experience:</span> Sustained
                      stamina and easier breathing.
                    </p>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-405">
                      <span className="font-medium" data-id="element-406">Why it works:</span>{' '}
                      Cordycepin, adenosine, and sterols boost ATP production
                      and oxygen use.
                    </p>
                    <p className="text-xs font-medium text-gray-500 mb-2" data-id="element-407">
                      Key Compounds:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-408">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-409">
                        Cordycepin
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-410">
                        Adenosine
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-411">
                        Sterols
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-412">
                    <div className="flex items-center gap-4 mb-3" data-id="element-413">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full" data-id="element-414">
                        <HeartPulseIcon className="w-5 h-5" data-id="element-415" />
                      </div>
                      <div data-id="element-416">
                        <h3 className="font-bold" data-id="element-417">Circulatory Support</h3>
                        <p className="text-xs text-gray-500" data-id="element-418">
                          Oxygen on demand
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-419">
                      <span className="font-medium" data-id="element-420">Experience:</span> Enhanced
                      endurance and cardiovascular efficiency.
                    </p>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-421">
                      <span className="font-medium" data-id="element-422">Why it works:</span>{' '}
                      Adenosine analogs improve blood flow and oxygen delivery.
                    </p>
                    <p className="text-xs font-medium text-gray-500 mb-2" data-id="element-423">
                      Key Compounds:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-424">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-425">
                        Adenosine analogs
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-426">
                    <div className="flex items-center gap-4 mb-3" data-id="element-427">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full" data-id="element-428">
                        <div className="w-5 h-5" data-id="element-429" />
                      </div>
                      <div data-id="element-430">
                        <h3 className="font-bold" data-id="element-431">Respiratory Health</h3>
                        <p className="text-xs text-gray-500" data-id="element-432">
                          Breathe with ease
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-433">
                      <span className="font-medium" data-id="element-434">Experience:</span> Deeper,
                      more efficient breathing under stress.
                    </p>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-435">
                      <span className="font-medium" data-id="element-436">Why it works:</span>{' '}
                      Cordycepin and polysaccharides expand lung capacity and
                      oxygen uptake.
                    </p>
                    <p className="text-xs font-medium text-gray-500 mb-2" data-id="element-437">
                      Key Compounds:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-438">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-439">
                        Cordycepin
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-440">
                        Polysaccharides
                      </span>
                    </div>
                  </div>
                </div>}
            </div>
            <div data-id="element-441">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'immunonutrients' ? '' : 'immunonutrients')} data-id="element-442">
                <div className="flex items-center gap-3" data-id="element-443">
                  <ShieldPlusIcon className="w-5 h-5" data-id="element-444" />
                  <h2 className="text-xl font-bold" data-id="element-445">The Resilience Matrix</h2>
                </div>
                <span data-id="element-446">{activeSection === 'immunonutrients' ? '−' : '+'}</span>
              </button>
              {activeSection === 'immunonutrients' && <div className="p-4 space-y-8" data-id="element-447">
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-448">
                    <div className="flex items-center gap-4 mb-3" data-id="element-449">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full" data-id="element-450">
                        <ShieldPlusIcon className="w-5 h-5" data-id="element-451" />
                      </div>
                      <div data-id="element-452">
                        <h3 className="font-bold" data-id="element-453">Immunonutrients</h3>
                        <p className="text-xs text-gray-500" data-id="element-454">
                          Cellular defense
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-455">
                      Experience resilience under strain and reduced burnout.
                      Beta-glucans modulate immune signaling.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-456">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-457">
                        Beta-glucans
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-458">
                        Glycoproteins
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-459">
                    <div className="flex items-center gap-4 mb-3" data-id="element-460">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full" data-id="element-461">
                        <CircleIcon className="w-5 h-5" data-id="element-462" />
                      </div>
                      <div data-id="element-463">
                        <h3 className="font-bold" data-id="element-464">Adaptogens</h3>
                        <p className="text-xs text-gray-500" data-id="element-465">
                          Stress equilibrium
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-466">
                      Feel centered during high-pressure situations. Triterpenes
                      harmonize the HPA axis.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-467">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-468">
                        Triterpenes
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-469">
                        Polysaccharides
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-470">
                    <div className="flex items-center gap-4 mb-3" data-id="element-471">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full" data-id="element-472">
                        <FlaskConicalIcon className="w-5 h-5" data-id="element-473" />
                      </div>
                      <div data-id="element-474">
                        <h3 className="font-bold" data-id="element-475">Hormonal Support</h3>
                        <p className="text-xs text-gray-500" data-id="element-476">
                          Metabolic harmony
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-477">
                      Experience stable energy levels and metabolic balance.
                      Sterols enhance hormone signaling.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-478">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-479">
                        Sterols
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-480">
                        Adenosine derivatives
                      </span>
                    </div>
                  </div>
                </div>}
            </div>
            <div data-id="element-481">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'antioxidants' ? '' : 'antioxidants')} data-id="element-482">
                <div className="flex items-center gap-3" data-id="element-483">
                  <ShieldIcon className="w-5 h-5" data-id="element-484" />
                  <h2 className="text-xl font-bold" data-id="element-485">The Protection Network</h2>
                </div>
                <span data-id="element-486">{activeSection === 'antioxidants' ? '−' : '+'}</span>
              </button>
              {activeSection === 'antioxidants' && <div className="p-4 space-y-8" data-id="element-487">
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-488">
                    <div className="flex items-center gap-4 mb-3" data-id="element-489">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full" data-id="element-490">
                        <ShieldIcon className="w-5 h-5" data-id="element-491" />
                      </div>
                      <div data-id="element-492">
                        <h3 className="font-bold" data-id="element-493">Antioxidants</h3>
                        <p className="text-xs text-gray-500" data-id="element-494">Cellular shield</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-495">
                      Experience reduced fatigue with steady energy flow.
                      Phenolic compounds neutralize free radicals.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-496">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-497">
                        Phenolic compounds
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-498">
                        Cordycepin
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-499">
                    <div className="flex items-center gap-4 mb-3" data-id="element-500">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full" data-id="element-501">
                        <HeartPulseIcon className="w-5 h-5" data-id="element-502" />
                      </div>
                      <div data-id="element-503">
                        <h3 className="font-bold" data-id="element-504">Anti-Inflammatory</h3>
                        <p className="text-xs text-gray-500" data-id="element-505">Rapid recovery</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-506">
                      Feel joints and muscles recover more quickly. Cordycepin
                      modulates inflammatory pathways.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-507">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-508">
                        Cordycepin
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-509">
                        Sterols
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-510">
                    <div className="flex items-center gap-4 mb-3" data-id="element-511">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full" data-id="element-512">
                        <BrainIcon className="w-5 h-5" data-id="element-513" />
                      </div>
                      <div data-id="element-514">
                        <h3 className="font-bold" data-id="element-515">Cognitive Support</h3>
                        <p className="text-xs text-gray-500" data-id="element-516">Mental clarity</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-517">
                      Experience sharper focus and reduced mental fatigue.
                      Nucleosides optimize neurotransmitter balance.
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-518">
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-519">
                        Cordycepin
                      </span>
                      <span className="inline-block px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium" data-id="element-520">
                        Nucleosides
                      </span>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-16 px-4 bg-black text-white text-center" data-id="element-521">
        <div className="max-w-4xl mx-auto" data-id="element-522">
          <h2 className="text-3xl font-bold mb-6" data-id="element-523">
            Experience Cordyceps Spora
          </h2>
          <p className="text-xl max-w-2xl mx-auto mb-8" data-id="element-524">
            Non-caffeinated energy that fuels your day with sustained stamina
            and cellular support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-525">
            <Link to="/#products" className="bg-red-500 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-red-600 transition-colors" data-id="element-526">
              Get Spora
            </Link>
            <Link to="/how-it-works" className="bg-transparent text-white border border-white px-8 py-3 rounded-full text-sm font-medium hover:bg-white/10 transition-colors" data-id="element-527">
              Learn The Ritual
            </Link>
            <Link to="/" className="bg-white text-black px-8 py-3 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center" data-id="element-528">
              <span data-id="element-529">Explore All Varieties</span>
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-530" />
            </Link>
          </div>
        </div>
      </section>
    </div>;
};
export default Cordyceps;